import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Navigate, Link, useLocation, useNavigate } from "react-router-dom";
import { PublicClientApplication } from "@azure/msal-browser";
import { ReactComponent as EyeOpen } from "../../assets/icons/eye.svg";
import { ReactComponent as EyeClosed } from "../../assets/icons/eye-slash.svg";
import { useTheme } from "../../context/ThemeContext";
import { useAuth } from "../../context/auth-context";

declare global {
  interface Window {
    google?: any;
    AppleID?: any;
  }
}

const API_BASE = "https://api.logvi-havox.com";
const DEVICE_ID_KEY = "logvi_device_id";
const REMEMBER_KEY = "eva_remember_me";

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || "";
const MICROSOFT_CLIENT_ID = process.env.REACT_APP_MICROSOFT_CLIENT_ID || "";
const MICROSOFT_TENANT_ID =
  process.env.REACT_APP_MICROSOFT_TENANT_ID || "common";
const APPLE_CLIENT_ID = process.env.REACT_APP_APPLE_CLIENT_ID || "";
const APPLE_REDIRECT_URI =
  process.env.REACT_APP_APPLE_REDIRECT_URI || `${window.location.origin}/login`;
const GITHUB_CLIENT_ID = process.env.REACT_APP_GITHUB_CLIENT_ID || "";
const GITHUB_REDIRECT_URI =
  process.env.REACT_APP_GITHUB_REDIRECT_URI ||
  `${window.location.origin}/login`;

function getOrCreateDeviceId(): string {
  try {
    let id = localStorage.getItem(DEVICE_ID_KEY);

    if (!id) {
      if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
        id = crypto.randomUUID();
      } else {
        id = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      }

      localStorage.setItem(DEVICE_ID_KEY, id);
    }

    return id;
  } catch {
    return "unknown-device";
  }
}

function useViewportWidth(): number {
  const [width, setWidth] = useState<number>(() => {
    if (typeof window === "undefined") return 1440;
    return window.innerWidth;
  });

  useEffect(() => {
    if (typeof window === "undefined") return;

    const update = () => setWidth(window.innerWidth);

    update();
    window.addEventListener("resize", update);
    window.addEventListener("orientationchange", update);

    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("orientationchange", update);
    };
  }, []);

  return width;
}

function useExternalScript(src: string, id: string) {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!src) {
      setReady(false);
      return;
    }

    const existing = document.getElementById(id) as HTMLScriptElement | null;

    if (existing) {
      if (existing.dataset.loaded === "true") {
        setReady(true);
        return;
      }

      const onLoad = () => {
        existing.dataset.loaded = "true";
        setReady(true);
      };

      const onError = () => setReady(false);

      existing.addEventListener("load", onLoad);
      existing.addEventListener("error", onError);

      return () => {
        existing.removeEventListener("load", onLoad);
        existing.removeEventListener("error", onError);
      };
    }

    const script = document.createElement("script");
    script.id = id;
    script.src = src;
    script.async = true;
    script.defer = true;

    script.onload = () => {
      script.dataset.loaded = "true";
      setReady(true);
    };

    script.onerror = () => {
      setReady(false);
    };

    document.body.appendChild(script);

    return () => {};
  }, [src, id]);

  return ready;
}

type SocialProvider = "google" | "apple" | "github" | "microsoft" | "fido2";

const GoogleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 48 48" aria-hidden="true">
    <path
      fill="#FFC107"
      d="M43.611 20.083H42V20H24v8h11.303C33.655 32.657 29.195 36 24 36c-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.277 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"
    />
    <path
      fill="#FF3D00"
      d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.277 4 24 4c-7.682 0-14.344 4.337-17.694 10.691z"
    />
    <path
      fill="#4CAF50"
      d="M24 44c5.093 0 9.786-1.947 13.346-5.118l-6.19-5.238C29.083 35.091 26.624 36 24 36c-5.174 0-9.623-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"
    />
    <path
      fill="#1976D2"
      d="M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.147 5.645l.003-.002 6.19 5.238C36.908 39.18 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"
    />
  </svg>
);

const AppleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
    <path
      fill="#111111"
      d="M16.365 1.43c0 1.14-.466 2.24-1.18 3.02-.77.84-2.03 1.49-3.08 1.41-.13-1.1.39-2.26 1.14-3.03.76-.79 2.09-1.38 3.12-1.4zM20.93 17.3c-.52 1.19-.77 1.72-1.44 2.76-.93 1.45-2.24 3.25-3.87 3.26-1.45.01-1.82-.95-3.79-.94-1.97.01-2.39.95-3.84.95-1.63.01-2.88-1.6-3.81-3.05-2.61-4.04-2.88-8.77-1.27-11.23 1.15-1.77 2.96-2.8 4.67-2.8 1.75 0 2.85.96 4.29.96 1.39 0 2.23-.96 4.28-.96 1.53 0 3.15.84 4.29 2.29-3.78 2.07-3.17 7.49.49 8.76z"
    />
  </svg>
);

const GithubIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
    <path
      fill="#111111"
      d="M12 .5C5.65.5.5 5.65.5 12.01c0 5.09 3.29 9.41 7.86 10.94.58.1.79-.25.79-.56 0-.27-.01-1.16-.02-2.1-3.2.7-3.88-1.36-3.88-1.36-.52-1.34-1.28-1.69-1.28-1.69-1.04-.72.08-.71.08-.71 1.15.08 1.76 1.18 1.76 1.18 1.02 1.76 2.68 1.25 3.34.95.1-.74.4-1.25.73-1.53-2.55-.29-5.24-1.28-5.24-5.69 0-1.26.45-2.3 1.18-3.11-.12-.29-.51-1.46.11-3.04 0 0 .97-.31 3.19 1.19a10.97 10.97 0 0 1 5.81 0c2.22-1.5 3.18-1.19 3.18-1.19.63 1.58.24 2.75.12 3.04.74.81 1.18 1.85 1.18 3.11 0 4.42-2.69 5.39-5.25 5.67.41.35.78 1.04.78 2.11 0 1.52-.01 2.74-.01 3.11 0 .31.2.67.8.56A11.52 11.52 0 0 0 23.5 12C23.5 5.65 18.35.5 12 .5z"
    />
  </svg>
);

const MicrosoftIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
    <rect x="2" y="2" width="9" height="9" fill="#F25022" />
    <rect x="13" y="2" width="9" height="9" fill="#7FBA00" />
    <rect x="2" y="13" width="9" height="9" fill="#00A4EF" />
    <rect x="13" y="13" width="9" height="9" fill="#FFB900" />
  </svg>
);

const Fido2Icon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
    <defs>
      <linearGradient id="fidoGradient" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#00C0B7" />
        <stop offset="100%" stopColor="#1A67BF" />
      </linearGradient>
    </defs>
    <path
      d="M12 2L4 5v6c0 5.25 3.44 9.92 8 11 4.56-1.08 8-5.75 8-11V5l-8-3z"
      fill="url(#fidoGradient)"
    />
    <circle cx="12" cy="10" r="2.2" fill="#ffffff" />
    <rect x="11.2" y="12" width="1.6" height="4" rx="0.8" fill="#ffffff" />
  </svg>
);

const providerMeta: Record<
  SocialProvider,
  { label: string; icon: React.ReactNode }
> = {
  google: { label: "Google", icon: <GoogleIcon /> },
  apple: { label: "Apple", icon: <AppleIcon /> },
  github: { label: "GitHub", icon: <GithubIcon /> },
  microsoft: { label: "Microsoft", icon: <MicrosoftIcon /> },
  fido2: { label: "FIDO2", icon: <Fido2Icon /> },
};

const SocialButton: React.FC<{
  provider: SocialProvider;
  onClick: () => void;
  disabled?: boolean;
  compact?: boolean;
}> = ({ provider, onClick, disabled, compact }) => {
  const meta = providerMeta[provider];

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        width: "100%",
        minHeight: compact ? 44 : 42,
        height: compact ? 44 : 42,
        borderRadius: compact ? 9 : 8,
        border: "1px solid rgba(15, 23, 42, 0.11)",
        background: "#FFFFFF",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 8,
        cursor: disabled ? "not-allowed" : "pointer",
        transition:
          "transform .16s ease, border-color .16s ease, background .16s ease, box-shadow .16s ease",
        fontFamily: "Poppins, sans-serif",
        fontSize: compact ? 12.5 : 12,
        fontWeight: 650,
        color: "#111827",
        padding: "0 12px",
        opacity: disabled ? 0.55 : 1,
        boxSizing: "border-box",
        overflow: "hidden",
        boxShadow: compact
          ? "0 1px 2px rgba(15, 23, 42, 0.05)"
          : "0 1px 2px rgba(15, 23, 42, 0.04)",
      }}
      onMouseEnter={(e) => {
        if (disabled) return;
        e.currentTarget.style.transform = "translateY(-1px)";
        e.currentTarget.style.borderColor = "rgba(14, 50, 58, 0.28)";
        e.currentTarget.style.background = "#FFFFFF";
        e.currentTarget.style.boxShadow = "0 8px 18px rgba(15, 23, 42, 0.08)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "translateY(0)";
        e.currentTarget.style.borderColor = "rgba(15, 23, 42, 0.11)";
        e.currentTarget.style.background = "#FFFFFF";
        e.currentTarget.style.boxShadow = compact
          ? "0 1px 2px rgba(15, 23, 42, 0.05)"
          : "0 1px 2px rgba(15, 23, 42, 0.04)";
      }}
    >
      <span
        style={{
          width: 18,
          height: 18,
          minWidth: 18,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        {meta.icon}
      </span>

      <span
        style={{
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {meta.label}
      </span>
    </button>
  );
};

const isMsalCryptoAvailable =
  typeof window !== "undefined" &&
  typeof window.crypto !== "undefined" &&
  typeof window.crypto.subtle !== "undefined";

const isLocalhost =
  window.location.hostname === "localhost" ||
  window.location.hostname === "127.0.0.1";

const isHttps = window.location.protocol === "https:";

const canUseMsal = isMsalCryptoAvailable && (isHttps || isLocalhost);

const msalConfig = {
  auth: {
    clientId: MICROSOFT_CLIENT_ID,
    authority: `https://login.microsoftonline.com/${MICROSOFT_TENANT_ID}`,
    redirectUri: `${window.location.origin}/login`,
  },
  cache: {
    cacheLocation: "sessionStorage" as const,
    storeAuthStateInCookie: false,
  },
};

const msalInstance = canUseMsal
  ? new PublicClientApplication(msalConfig)
  : null;

const LoginForm: React.FC = () => {
  const { refresh } = useAuth();
  const { setTheme, setSystemTitle } = useTheme();
  const viewportWidth = useViewportWidth();

  const isNarrow = viewportWidth <= 980;
  const isPhone = viewportWidth <= 560;
  const isVerySmall = viewportWidth <= 380;

  const location = useLocation();
  const navigate = useNavigate();

  // Botões sociais temporariamente comentados/desabilitados.
  // Mantive as integrações abaixo comentadas para reativação futura, se necessário.
  //
  // const googleReady = useExternalScript(
  //   "https://accounts.google.com/gsi/client",
  //   "google-gsi-script"
  // );
  //
  // const appleReady = useExternalScript(
  //   "https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js",
  //   "apple-auth-script"
  // );

  const googleReady = false;
  const appleReady = false;

  const [emailOrPhone, setEmailOrPhone] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState<boolean>(() => {
    try {
      const saved = localStorage.getItem(REMEMBER_KEY);
      return saved ? JSON.parse(saved) === true : false;
    } catch {
      return false;
    }
  });

  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [socialLoading, setSocialLoading] = useState<SocialProvider | null>(
    null
  );

  const [feedback, setFeedback] = useState<{
    type: "error" | "success";
    msg: string;
  } | null>(null);

  const googleInitializedRef = useRef(false);
  const googleSdkButtonRef = useRef<HTMLDivElement | null>(null);
  const googleSdkClickableRef = useRef<HTMLElement | null>(null);

  const email = useMemo(() => emailOrPhone.trim(), [emailOrPhone]);

  useEffect(() => {
    try {
      localStorage.setItem(REMEMBER_KEY, JSON.stringify(remember));
    } catch {}
  }, [remember]);

  const goToHome = useCallback(() => {
    navigate("/acess/home", { replace: true });
  }, [navigate]);

  const applyLoginResponse = useCallback(
    async (data: any) => {
      if (data?.theme) setTheme(data.theme);
      if (data?.systemTitle) setSystemTitle(data.systemTitle);

      if (data?.accessToken) {
        try {
          localStorage.setItem("access_token", data.accessToken);
        } catch {}
      }

      await refresh();
    },
    [refresh, setTheme, setSystemTitle]
  );

  const postSocialLogin = useCallback(
    async (
      provider: "google" | "apple" | "github" | "microsoft",
      payload: Record<string, any>
    ) => {
      const deviceId = getOrCreateDeviceId();
      const res = await fetch(`${API_BASE}/auth/login-hml`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          provider,
          remember,
          deviceId,
          ...payload,
        }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(data?.erro || "Falha no login social.");
      }

      await applyLoginResponse(data);

      setFeedback({
        type: "success",
        msg: "Login realizado com sucesso.",
      });

      goToHome();
    },
    [remember, applyLoginResponse, goToHome]
  );

  useEffect(() => {
    if (!googleReady || !GOOGLE_CLIENT_ID) return;
    if (!window.google?.accounts?.id) return;
    if (!googleSdkButtonRef.current) return;

    if (!googleInitializedRef.current) {
      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: async (response: any) => {
          if (!response?.credential) {
            setFeedback({
              type: "error",
              msg: "Google não retornou credencial válida.",
            });
            setSocialLoading(null);
            return;
          }

          try {
            setFeedback(null);
            setSocialLoading("google");
            await postSocialLogin("google", { idToken: response.credential });
          } catch (err: any) {
            setFeedback({
              type: "error",
              msg: err?.message || "Falha ao autenticar com Google.",
            });
          } finally {
            setSocialLoading(null);
          }
        },
        auto_select: false,
        cancel_on_tap_outside: true,
      });

      googleInitializedRef.current = true;
    }

    googleSdkButtonRef.current.innerHTML = "";
    googleSdkClickableRef.current = null;

    window.google.accounts.id.renderButton(googleSdkButtonRef.current, {
      theme: "outline",
      size: "large",
      type: "standard",
      shape: "rectangular",
      text: "signin_with",
      width: 220,
      logo_alignment: "left",
    });

    requestAnimationFrame(() => {
      if (!googleSdkButtonRef.current) return;

      const clickable =
        googleSdkButtonRef.current.querySelector('[role="button"]') ||
        googleSdkButtonRef.current.querySelector("div") ||
        googleSdkButtonRef.current.firstElementChild;

      googleSdkClickableRef.current = clickable as HTMLElement | null;
    });
  }, [googleReady, postSocialLogin]);

  useEffect(() => {
    if (!appleReady || !APPLE_CLIENT_ID || !window.AppleID?.auth) return;

    try {
      window.AppleID.auth.init({
        clientId: APPLE_CLIENT_ID,
        scope: "name email",
        redirectURI: APPLE_REDIRECT_URI,
        state: "apple-login",
        usePopup: true,
      });
    } catch {}
  }, [appleReady]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const provider = params.get("social_provider");
    const code = params.get("code");
    const error = params.get("error");
    const errorDescription = params.get("error_description");

    if (error) {
      setFeedback({
        type: "error",
        msg: decodeURIComponent(errorDescription || "Falha no login social."),
      });
      return;
    }

    if (provider === "github" && code) {
      (async () => {
        try {
          setSocialLoading("github");
          await postSocialLogin("github", { code });
          navigate("/acess/home", { replace: true });
        } catch (err: any) {
          setFeedback({
            type: "error",
            msg: err?.message || "Falha ao autenticar com GitHub.",
          });
        } finally {
          setSocialLoading(null);
        }
      })();
    }
  }, [location.search, navigate, postSocialLogin]);

  const validate = useCallback(() => {
    if (!email || !password) {
      setFeedback({
        type: "error",
        msg: "Preencha os campos para realizar o acesso.",
      });
      return false;
    }

    if (!email.includes("@")) {
      setFeedback({
        type: "error",
        msg: "Informe um e-mail válido para continuar.",
      });
      return false;
    }

    return true;
  }, [email, password]);

  const handleSubmit = useCallback(async () => {
    if (loading) return;

    setFeedback(null);

    if (!validate()) return;

    try {
      setLoading(true);

      const deviceId = getOrCreateDeviceId();
      const res = await fetch(`${API_BASE}/auth/login-hml`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          email,
          senha: password,
          remember,
          deviceId,
        }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        setFeedback({
          type: "error",
          msg:
            data?.erro ||
            (res.status === 401 ? "Credenciais inválidas." : "Falha no login."),
        });
        return;
      }

      await applyLoginResponse(data);

      setFeedback({
        type: "success",
        msg: "Login realizado com sucesso.",
      });

      goToHome();
    } catch (err: any) {
      setFeedback({
        type: "error",
        msg: err?.message || "Não foi possível conectar ao servidor.",
      });
    } finally {
      setLoading(false);
    }
  }, [
    loading,
    validate,
    email,
    password,
    remember,
    applyLoginResponse,
    goToHome,
  ]);

  const handleMicrosoftLogin = useCallback(async () => {
    if (!MICROSOFT_CLIENT_ID) {
      setFeedback({
        type: "error",
        msg: "REACT_APP_MICROSOFT_CLIENT_ID não configurado.",
      });
      return;
    }

    try {
      setFeedback(null);

      if (!msalInstance) {
        setFeedback({
          type: "error",
          msg: "Login Microsoft indisponível neste ambiente local via IP. Para testar no iPhone, acesse por HTTPS, localhost ou use outro método de login.",
        });
        return;
      }

      setSocialLoading("microsoft");

      await msalInstance.initialize();

      const response = await msalInstance.loginPopup({
        scopes: ["openid", "profile", "email"],
        prompt: "select_account",
      });

      const idToken = response.idToken;

      if (!idToken) {
        throw new Error("Microsoft não retornou idToken.");
      }

      await postSocialLogin("microsoft", { idToken });
    } catch (err: any) {
      setFeedback({
        type: "error",
        msg: err?.message || "Falha ao autenticar com Microsoft.",
      });
    } finally {
      setSocialLoading(null);
    }
  }, [postSocialLogin]);

  const handleAppleLogin = useCallback(async () => {
    if (!APPLE_CLIENT_ID) {
      setFeedback({
        type: "error",
        msg: "REACT_APP_APPLE_CLIENT_ID não configurado.",
      });
      return;
    }

    if (!window.AppleID?.auth) {
      setFeedback({
        type: "error",
        msg: "SDK da Apple ainda não carregou.",
      });
      return;
    }

    try {
      setFeedback(null);
      setSocialLoading("apple");

      const response = await window.AppleID.auth.signIn();
      const idToken = response?.authorization?.id_token;

      if (!idToken) {
        throw new Error("Apple não retornou idToken.");
      }

      await postSocialLogin("apple", { idToken });
    } catch (err: any) {
      setFeedback({
        type: "error",
        msg: err?.message || "Falha ao autenticar com Apple.",
      });
    } finally {
      setSocialLoading(null);
    }
  }, [postSocialLogin]);

  const handleGithubLogin = useCallback(() => {
    if (!GITHUB_CLIENT_ID) {
      setFeedback({
        type: "error",
        msg: "REACT_APP_GITHUB_CLIENT_ID não configurado.",
      });
      return;
    }

    const state =
      typeof crypto !== "undefined" && "randomUUID" in crypto
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

    sessionStorage.setItem("github_oauth_state", state);

    const redirectUri = new URL(GITHUB_REDIRECT_URI);
    redirectUri.searchParams.set("social_provider", "github");

    const githubUrl = new URL("https://github.com/login/oauth/authorize");
    githubUrl.searchParams.set("client_id", GITHUB_CLIENT_ID);
    githubUrl.searchParams.set("redirect_uri", redirectUri.toString());
    githubUrl.searchParams.set("scope", "read:user user:email");
    githubUrl.searchParams.set("state", state);

    setSocialLoading("github");
    window.location.href = githubUrl.toString();
  }, []);

  const handleGoogleLogin = useCallback(() => {
    if (loading || socialLoading) return;

    if (!GOOGLE_CLIENT_ID) {
      setFeedback({
        type: "error",
        msg: "REACT_APP_GOOGLE_CLIENT_ID não configurado.",
      });
      return;
    }

    if (!googleReady || !googleSdkClickableRef.current) {
      setFeedback({
        type: "error",
        msg: "SDK do Google ainda não está pronto.",
      });
      return;
    }

    setFeedback(null);
    googleSdkClickableRef.current.click();
  }, [loading, socialLoading, googleReady]);

  const handleSocialClick = useCallback(
    async (provider: SocialProvider) => {
      if (loading || socialLoading) return;

      if (provider === "google") return handleGoogleLogin();
      if (provider === "microsoft") return handleMicrosoftLogin();
      if (provider === "apple") return handleAppleLogin();
      if (provider === "github") return handleGithubLogin();

      setFeedback({
        type: "error",
        msg: "FIDO2 ainda não foi implementado nesta tela.",
      });
    },
    [
      loading,
      socialLoading,
      handleGoogleLogin,
      handleMicrosoftLogin,
      handleAppleLogin,
      handleGithubLogin,
    ]
  );

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && !loading) {
      handleSubmit();
    }
  };

  const pageStyle: React.CSSProperties = {
    minHeight: "100dvh",
    width: "100%",
    position: "relative",
    overflowX: "hidden",
    overflowY: isNarrow ? "auto" : "hidden",
    background: isNarrow
      ? "#FFFFFF"
      : "linear-gradient(135deg, #061B20 0%, #08272E 38%, #0B333B 100%)",
    display: "flex",
    alignItems: isNarrow ? "stretch" : "center",
    justifyContent: "center",
    padding: isNarrow ? 0 : "28px 24px",
    boxSizing: "border-box",
    fontFamily: "Poppins, sans-serif",
  };

  const backgroundLayerStyle: React.CSSProperties = {
    position: "absolute",
    inset: 0,
    overflow: "hidden",
    pointerEvents: "none",
    display: isNarrow ? "none" : "block",
  };

  const gridOverlayStyle: React.CSSProperties = {
    position: "absolute",
    inset: 0,
    opacity: 0.22,
    backgroundImage:
      "linear-gradient(rgba(255,255,255,0.045) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.045) 1px, transparent 1px)",
    backgroundSize: "42px 42px",
    maskImage:
      "linear-gradient(90deg, transparent 0%, #000 18%, #000 82%, transparent 100%)",
    WebkitMaskImage:
      "linear-gradient(90deg, transparent 0%, #000 18%, #000 82%, transparent 100%)",
  };

  const radialLightStyle: React.CSSProperties = {
    position: "absolute",
    width: 720,
    height: 720,
    borderRadius: "50%",
    top: -260,
    right: -260,
    background:
      "radial-gradient(circle, rgba(0,192,183,0.22) 0%, rgba(0,192,183,0.10) 32%, rgba(0,192,183,0) 68%)",
  };

  const radialDarkStyle: React.CSSProperties = {
    position: "absolute",
    width: 760,
    height: 760,
    borderRadius: "50%",
    bottom: -360,
    left: -280,
    background:
      "radial-gradient(circle, rgba(0,0,0,0.28) 0%, rgba(0,0,0,0.12) 34%, rgba(0,0,0,0) 70%)",
  };

  const diagonalLineOneStyle: React.CSSProperties = {
    position: "absolute",
    width: "72%",
    height: 1,
    left: "-10%",
    top: "27%",
    transform: "rotate(-18deg)",
    background:
      "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.12) 44%, transparent 100%)",
  };

  const diagonalLineTwoStyle: React.CSSProperties = {
    position: "absolute",
    width: "64%",
    height: 1,
    right: "-14%",
    bottom: "25%",
    transform: "rotate(-18deg)",
    background:
      "linear-gradient(90deg, transparent 0%, rgba(0,192,183,0.18) 45%, transparent 100%)",
  };

  const smallAccentOneStyle: React.CSSProperties = {
    position: "absolute",
    width: 68,
    height: 68,
    right: "20%",
    top: "15%",
    borderRadius: 18,
    transform: "rotate(18deg)",
    border: "1px solid rgba(0,192,183,0.20)",
    background: "rgba(0,192,183,0.045)",
  };

  const smallAccentTwoStyle: React.CSSProperties = {
    position: "absolute",
    width: 84,
    height: 84,
    left: "18%",
    bottom: "16%",
    borderRadius: 22,
    transform: "rotate(-18deg)",
    border: "1px solid rgba(255,255,255,0.11)",
    background: "rgba(255,255,255,0.025)",
  };

  const cardStyle: React.CSSProperties = {
    width: "100%",
    maxWidth: isNarrow ? "none" : 392,
    minHeight: isNarrow ? "100dvh" : "auto",
    borderRadius: isNarrow ? 0 : 14,
    background: "#FFFFFF",
    border: isNarrow ? "none" : "1px solid rgba(255,255,255,0.42)",
    padding: isNarrow
      ? isPhone
        ? isVerySmall
          ? "22px 14px 18px"
          : "26px 18px 20px"
        : "34px 48px 28px"
      : "22px 22px 18px",
    boxSizing: "border-box",
    zIndex: 2,
    backdropFilter: isNarrow ? "none" : "blur(18px)",
    WebkitBackdropFilter: isNarrow ? "none" : "blur(18px)",
    boxShadow: isNarrow
      ? "none"
      : "0 28px 70px rgba(0,0,0,0.28), 0 1px 0 rgba(255,255,255,0.78) inset",
    position: "relative",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    justifyContent: isNarrow ? "flex-start" : "initial",
  };

  const cardInnerStyle: React.CSSProperties = {
    width: "100%",
    maxWidth: isNarrow ? (isPhone ? "100%" : 540) : "none",
    margin: isNarrow ? "0 auto" : 0,
  };

  const cardAccentStyle: React.CSSProperties = {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: isNarrow ? 0 : 3,
    background: "linear-gradient(90deg, #0E323A 0%, #00C0B7 50%, #0E323A 100%)",
  };

  const topMobileLineStyle: React.CSSProperties = {
    display: isNarrow ? "block" : "none",
    width: 42,
    height: 3,
    borderRadius: 999,
    background: "#0E323A",
    marginBottom: 22,
  };

  const formTitleStyle: React.CSSProperties = {
    margin: "0 0 6px",
    color: "#0F172A",
    fontSize: isPhone ? 22 : isNarrow ? 25 : 24,
    lineHeight: 1.12,
    fontWeight: 760,
    letterSpacing: "-0.025em",
  };

  const subtitleStyle: React.CSSProperties = {
    margin: "0 0 18px",
    color: "#64748B",
    fontSize: isPhone ? 12 : 12.5,
    lineHeight: 1.5,
    fontWeight: 500,
  };

  const labelStyle: React.CSSProperties = {
    display: "block",
    fontSize: 11.5,
    fontWeight: 700,
    color: "#334155",
    marginBottom: 6,
  };

  const fieldGroupStyle: React.CSSProperties = {
    width: "100%",
    marginBottom: isNarrow ? 12 : 11,
  };

  const inputWrapStyleBase: React.CSSProperties = {
    width: "100%",
    minHeight: isNarrow ? 46 : 44,
    borderRadius: isNarrow ? 9 : 8,
    border: "1px solid #D8E0E8",
    background: "#F8FAFC",
    display: "flex",
    alignItems: "center",
    padding: "0 12px",
    boxSizing: "border-box",
    position: "relative",
    transition:
      "border-color .16s ease, background .16s ease, box-shadow .16s ease",
  };

  const inputStyle: React.CSSProperties = {
    width: "100%",
    height: "100%",
    border: "none",
    outline: "none",
    background: "transparent",
    color: "#0F172A",
    fontSize: isPhone ? 16 : isNarrow ? 14 : 13,
    fontWeight: 550,
    fontFamily: "Poppins, sans-serif",
    paddingRight: 36,
  };
  const eyeButtonStyle: React.CSSProperties = {
    position: "absolute",
    right: 8,
    top: "50%",
    transform: "translateY(-50%)",
    width: 30,
    height: 30,
    borderRadius: 7,
    border: "none",
    background: "transparent",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    color: "#64748B",
  };

  const rememberRowStyle: React.CSSProperties = {
    width: "100%",
    display: "flex",
    alignItems: isPhone ? "flex-start" : "center",
    justifyContent: "space-between",
    gap: 9,
    marginTop: 2,
    marginBottom: isNarrow ? 14 : 13,
    flexDirection: isPhone ? "column" : "row",
  };

  const checkboxLabelStyle: React.CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    cursor: "pointer",
    userSelect: "none",
  };

  const checkboxInputStyle: React.CSSProperties = {
    position: "absolute",
    opacity: 0,
    width: 0,
    height: 0,
    pointerEvents: "none",
  };

  const checkboxBoxStyle: React.CSSProperties = {
    width: 17,
    height: 17,
    borderRadius: 5,
    border: remember ? "1px solid #0E323A" : "1px solid #CAD5E2",
    background: remember ? "#0E323A" : "#FFFFFF",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "all .16s ease",
    flexShrink: 0,
  };

  const checkIcon = (
    <svg width="11" height="11" viewBox="0 0 24 24" aria-hidden="true">
      <path
        d="M20 6L9 17l-5-5"
        fill="none"
        stroke="#ffffff"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );

  const linkStyle: React.CSSProperties = {
    color: "#0E323A",
    fontSize: 11.5,
    fontWeight: 700,
    textDecoration: "none",
  };

  const primaryButtonStyle: React.CSSProperties = {
    width: "100%",
    minHeight: isNarrow ? 46 : 44,
    borderRadius: isNarrow ? 9 : 8,
    border: "1px solid rgba(255,255,255,0.08)",
    cursor: loading ? "not-allowed" : "pointer",
    background: "linear-gradient(180deg, #0E323A 0%, #09262D 100%)",
    color: "#FFFFFF",
    fontFamily: "Poppins, sans-serif",
    fontSize: isNarrow ? 14 : 13.5,
    fontWeight: 750,
    letterSpacing: 0.15,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "transform .16s ease, box-shadow .16s ease, opacity .16s ease",
    marginBottom: 13,
    opacity: loading ? 0.78 : 1,
    boxShadow: "0 12px 22px rgba(14, 50, 58, 0.18)",
  };

  const dividerWrapStyle: React.CSSProperties = {
    width: "100%",
    display: "flex",
    alignItems: "center",
    gap: 9,
    margin: "7px 0 12px",
  };

  const dividerLineStyle: React.CSSProperties = {
    flex: 1,
    height: 1,
    background: "rgba(148, 163, 184, 0.24)",
  };

  const dividerTextStyle: React.CSSProperties = {
    fontSize: 10,
    fontWeight: 750,
    color: "#94A3B8",
    letterSpacing: 0.45,
    textTransform: "uppercase",
    whiteSpace: "nowrap",
  };

  const socialGridStyle: React.CSSProperties = {
    display: "grid",
    gridTemplateColumns: isPhone ? "1fr" : "1fr 1fr",
    gap: 8,
    width: "100%",
    alignItems: "stretch",
  };

  const bottomRowStyle: React.CSSProperties = {
    marginTop: 14,
    paddingTop: 13,
    borderTop: "1px solid rgba(148, 163, 184, 0.18)",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    flexWrap: "wrap",
  };

  const mutedTextStyle: React.CSSProperties = {
    color: "#64748B",
    fontSize: 11.5,
    lineHeight: 1.45,
  };

  const feedbackStyle = (type: "error" | "success"): React.CSSProperties => ({
    width: "100%",
    marginBottom: 12,
    borderRadius: 8,
    padding: "9px 10px",
    fontSize: 11.5,
    fontWeight: 650,
    lineHeight: 1.45,
    border:
      type === "error"
        ? "1px solid rgba(220, 38, 38, 0.14)"
        : "1px solid rgba(22, 163, 74, 0.14)",
    background:
      type === "error" ? "rgba(254, 242, 242, 1)" : "rgba(240, 253, 244, 1)",
    color: type === "error" ? "#B91C1C" : "#15803D",
  });

  return (
    <div style={pageStyle}>
      <style>
        {`

          html,
          body,
          #root {
            min-width: 0;
          }

          input,
          button {
            -webkit-tap-highlight-color: transparent;
          }

          @media (max-width: 980px) {
            body {
              background: #ffffff;
            }
          }
        `}
      </style>

      <div style={backgroundLayerStyle}>
        <div style={gridOverlayStyle} />
        <div style={radialLightStyle} />
        <div style={radialDarkStyle} />
        <div style={diagonalLineOneStyle} />
        <div style={diagonalLineTwoStyle} />
        <div style={smallAccentOneStyle} />
        <div style={smallAccentTwoStyle} />
      </div>

      <div style={cardStyle}>
        <div style={cardAccentStyle} />

        <div style={cardInnerStyle}>
          <div style={topMobileLineStyle} />

          <div style={formTitleStyle}>Entrar no Logvi</div>

          <div style={subtitleStyle}>
            Informe suas credenciais para acessar o ambiente operacional.
          </div>

          {feedback && (
            <div style={feedbackStyle(feedback.type)} role="alert">
              {feedback.msg}
            </div>
          )}

          <div style={fieldGroupStyle}>
            <label style={labelStyle}>E-mail corporativo</label>

            <div
              style={inputWrapStyleBase}
              onFocusCapture={(e) => {
                e.currentTarget.style.borderColor = "#0E323A";
                e.currentTarget.style.background = "#FFFFFF";
                e.currentTarget.style.boxShadow =
                  "0 0 0 3px rgba(14, 50, 58, 0.08)";
              }}
              onBlurCapture={(e) => {
                e.currentTarget.style.borderColor = "#D8E0E8";
                e.currentTarget.style.background = "#F8FAFC";
                e.currentTarget.style.boxShadow = "none";
              }}
            >
              <input
                type="text"
                value={emailOrPhone}
                onChange={(e) => setEmailOrPhone(e.target.value)}
                onKeyDown={onKeyDown}
                placeholder="seunome@dominio.com.br"
                autoComplete="username"
                style={inputStyle}
              />
            </div>
          </div>

          <div style={fieldGroupStyle}>
            <label style={labelStyle}>Senha</label>

            <div
              style={inputWrapStyleBase}
              onFocusCapture={(e) => {
                e.currentTarget.style.borderColor = "#0E323A";
                e.currentTarget.style.background = "#FFFFFF";
                e.currentTarget.style.boxShadow =
                  "0 0 0 3px rgba(14, 50, 58, 0.08)";
              }}
              onBlurCapture={(e) => {
                e.currentTarget.style.borderColor = "#D8E0E8";
                e.currentTarget.style.background = "#F8FAFC";
                e.currentTarget.style.boxShadow = "none";
              }}
            >
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyDown={onKeyDown}
                placeholder="Digite sua senha"
                autoComplete="current-password"
                style={inputStyle}
              />

              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
                style={eyeButtonStyle}
              >
                {showPassword ? <EyeClosed /> : <EyeOpen />}
              </button>
            </div>
          </div>

          <div style={rememberRowStyle}>
            <label htmlFor="rememberMe" style={checkboxLabelStyle}>
              <input
                id="rememberMe"
                type="checkbox"
                checked={remember}
                onChange={(e) => setRemember(e.target.checked)}
                style={checkboxInputStyle}
              />

              <span aria-hidden="true" style={checkboxBoxStyle}>
                {remember ? checkIcon : null}
              </span>

              <span
                style={{
                  color: "#525A66",
                  fontSize: 12,
                  fontWeight: 500,
                }}
              >
                Manter sessão ativa
              </span>
            </label>

            <Link to="/redefinir-senha" style={linkStyle}>
              Esqueceu sua senha?
            </Link>
          </div>

          <button
            type="button"
            onClick={handleSubmit}
            disabled={loading || !!socialLoading}
            style={primaryButtonStyle}
            onMouseEnter={(e) => {
              if (loading) return;
              e.currentTarget.style.transform = "translateY(-1px)";
              e.currentTarget.style.boxShadow =
                "0 16px 28px rgba(14, 50, 58, 0.24)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow =
                "0 12px 22px rgba(14, 50, 58, 0.18)";
            }}
          >
            {loading ? "Entrando..." : "Continuar"}
          </button>

          {/*
            Botões sociais temporariamente comentados.

            <div style={dividerWrapStyle}>
              <div style={dividerLineStyle} />
              <div style={dividerTextStyle}>ou</div>
              <div style={dividerLineStyle} />
            </div>

            <div style={socialGridStyle}>
              <SocialButton
                provider="google"
                compact={isNarrow}
                disabled={loading || !!socialLoading || !googleReady}
                onClick={() => handleSocialClick("google")}
              />

              <SocialButton
                provider="apple"
                compact={isNarrow}
                disabled={loading || !!socialLoading}
                onClick={() => handleSocialClick("apple")}
              />

              <SocialButton
                provider="github"
                compact={isNarrow}
                disabled={loading || !!socialLoading}
                onClick={() => handleSocialClick("github")}
              />

              <SocialButton
                provider="microsoft"
                compact={isNarrow}
                disabled={loading || !!socialLoading}
                onClick={() => handleSocialClick("microsoft")}
              />
            </div>

            <div style={dividerWrapStyle}>
              <div style={dividerLineStyle} />
              <span style={dividerTextStyle}>Acesso seguro</span>
              <div style={dividerLineStyle} />
            </div>

            <SocialButton
              provider="fido2"
              compact={isNarrow}
              disabled={loading || !!socialLoading}
              onClick={() => handleSocialClick("fido2")}
            />
          */}

          <div style={bottomRowStyle}>
            <div style={mutedTextStyle}>
              Primeiro acesso?{" "}
              <Link
                to="/cadastro"
                style={{
                  color: "#0E323A",
                  fontWeight: 750,
                  textDecoration: "none",
                }}
              >
                Realizar cadastro
              </Link>
            </div>
          </div>

          <div
            aria-hidden="true"
            style={{
              position: "fixed",
              left: -9999,
              top: -9999,
              width: 1,
              height: 1,
              opacity: 0,
              pointerEvents: "none",
              overflow: "hidden",
            }}
          >
            <div ref={googleSdkButtonRef} />
          </div>
        </div>
      </div>
    </div>
  );
};

const AcessLogin: React.FC = () => {
  const { sessao } = useAuth();

  if (sessao) {
    return <Navigate to="/acess/home" replace />;
  }

  return <LoginForm />;
};

export default AcessLogin;
